import React, { useState, useEffect, useCallback } from 'react';
import {
  CloudArrowUpIcon,
  CloudArrowDownIcon,
  TrashIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  XCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import {
  getSyncStatus,
  listAzureUsers,
  deleteAzureUser,
  syncUserToSnowflake,
} from '../services/azureSyncService';

/**
 * AzureSyncManager Component
 *
 * Admin interface for managing Azure Table Storage sync with Snowflake.
 * Part of UC-017: Azure Table Storage Sync for GDPR Admin
 *
 * Features:
 * - View sync statistics (total, synced, orphaned counts)
 * - List Azure Table users with sync status
 * - Filter by orphaned/synced status
 * - Delete orphan records (Azure Table only)
 * - Sync orphan records to Snowflake
 * - Dark mode support
 */
const AzureSyncManager = () => {
  // Status state
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  // Users list state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersTotal, setUsersTotal] = useState(0);
  const [filter, setFilter] = useState('orphaned'); // 'all', 'orphaned', 'synced'
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Action state
  const [actionLoading, setActionLoading] = useState(null); // email being acted on
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Confirmation modal
  const [confirmModal, setConfirmModal] = useState(null); // { type: 'delete'|'sync', email: string }

  // Fetch sync status
  const fetchStatus = useCallback(async () => {
    try {
      setLoadingStatus(true);
      const data = await getSyncStatus();
      setStatus(data);
    } catch (err) {
      setError(`Failed to load sync status: ${err.message}`);
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      setError('');
      const data = await listAzureUsers({ filter, search, limit: 100 });
      setUsers(data.users);
      setUsersTotal(data.total);
    } catch (err) {
      setError(`Failed to load users: ${err.message}`);
    } finally {
      setLoadingUsers(false);
    }
  }, [filter, search]);

  // Initial load
  useEffect(() => {
    fetchStatus();
    fetchUsers();
  }, [fetchStatus, fetchUsers]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  // Clear search
  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
  };

  // Delete orphan user
  const handleDelete = async (email) => {
    try {
      setActionLoading(email);
      setError('');
      await deleteAzureUser(email);
      setSuccessMessage(`Successfully deleted ${email} from Azure Table`);
      setConfirmModal(null);
      // Refresh data
      await Promise.all([fetchStatus(), fetchUsers()]);
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  // Sync user to Snowflake
  const handleSync = async (email) => {
    try {
      setActionLoading(email);
      setError('');
      await syncUserToSnowflake(email);
      setSuccessMessage(`Successfully synced ${email} to Snowflake`);
      setConfirmModal(null);
      // Refresh data
      await Promise.all([fetchStatus(), fetchUsers()]);
    } catch (err) {
      setError(`Sync failed: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  return (
    <div className="space-y-6">
      {/* Header with info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start">
          <InformationCircleIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm text-blue-700 dark:text-blue-300">
            <p className="font-medium mb-1">Azure Table Storage Sync</p>
            <p>
              The registration system stores users in Azure Table Storage. If a registration fails mid-process,
              users may exist in Azure Table but not in Snowflake, blocking re-registration with the same email.
            </p>
            <p className="mt-1">
              Use this tool to view orphaned records (Azure only) and either delete them or sync them to Snowflake.
            </p>
          </div>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700 dark:text-red-300">{error}</span>
            <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">
              <XCircleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
            <span className="text-green-700 dark:text-green-300">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Azure Table Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {loadingStatus ? '...' : status?.azure_table_total ?? '-'}
              </p>
            </div>
            <CloudArrowUpIcon className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Snowflake Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {loadingStatus ? '...' : status?.snowflake_total ?? '-'}
              </p>
            </div>
            <CloudArrowDownIcon className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Synced</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {loadingStatus ? '...' : status?.synced_count ?? '-'}
              </p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Orphaned</p>
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {loadingStatus ? '...' : status?.orphaned_count ?? '-'}
              </p>
            </div>
            <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Filter buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('orphaned')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === 'orphaned'
                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              Orphaned Only
            </button>
            <button
              onClick={() => setFilter('synced')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === 'synced'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              Synced Only
            </button>
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              All
            </button>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by email..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-[#FFC000] focus:border-transparent"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-[#FFC000] text-black rounded-md hover:bg-[#e6ac00] font-medium"
            >
              Search
            </button>
            {search && (
              <button
                type="button"
                onClick={clearSearch}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                Clear
              </button>
            )}
          </form>

          {/* Refresh */}
          <button
            onClick={() => { fetchStatus(); fetchUsers(); }}
            disabled={loadingUsers}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center"
          >
            <ArrowPathIcon className={`h-5 w-5 mr-1 ${loadingUsers ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Azure Table Users ({usersTotal})
          </h3>
        </div>

        {loadingUsers ? (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            No users found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Email Verified
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((user) => (
                  <tr key={user.email} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {user.organization_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.synced_to_snowflake ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          Synced
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                          <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                          Orphaned
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {user.email_verified ? 'Yes' : 'No'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {!user.synced_to_snowflake && (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => setConfirmModal({ type: 'sync', email: user.email })}
                            disabled={actionLoading === user.email}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50"
                            title="Sync to Snowflake"
                          >
                            <CloudArrowDownIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => setConfirmModal({ type: 'delete', email: user.email })}
                            disabled={actionLoading === user.email}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                            title="Delete from Azure Table"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              {confirmModal.type === 'delete' ? 'Delete User from Azure Table' : 'Sync User to Snowflake'}
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              {confirmModal.type === 'delete' ? (
                <>
                  Are you sure you want to delete <strong>{confirmModal.email}</strong> from Azure Table Storage?
                  This will allow the email to be used for a new registration.
                </>
              ) : (
                <>
                  Are you sure you want to sync <strong>{confirmModal.email}</strong> to Snowflake?
                  This will create a basic user profile in the database.
                </>
              )}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmModal(null)}
                disabled={actionLoading}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmModal.type === 'delete'
                  ? handleDelete(confirmModal.email)
                  : handleSync(confirmModal.email)
                }
                disabled={actionLoading}
                className={`px-4 py-2 rounded-md text-white font-medium flex items-center ${
                  confirmModal.type === 'delete'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                } disabled:opacity-50`}
              >
                {actionLoading === confirmModal.email && (
                  <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                )}
                {confirmModal.type === 'delete' ? 'Delete' : 'Sync'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AzureSyncManager;
